import { useState, useEffect } from 'react';
import { useAppDispatch } from '../../store/hooks';
import { sendMessageToUser } from '../../store/slices/adminSlice';
import toast from 'react-hot-toast';

const SendMessageModal = ({ isOpen, onClose, user }) => {
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    message_type: '',
    email_title: '',
    message: ''
  });

  const [errors, setErrors] = useState({
    message_type: '',
    email_title: '',
    message: ''
  });

  useEffect(() => {
    if (isOpen) {
      setFormData({
        message_type: '',
        email_title: '',
        message: ''
      });
      setErrors({
        message_type: '',
        email_title: '',
        message: ''
      });
    }
  }, [isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validate = () => {
    let isValid = true;
    const newErrors = {
      message_type: '',
      email_title: '',
      message: ''
    };

    if (!formData.message_type) {
      newErrors.message_type = 'Please select a message type.';
      isValid = false;
    }
    if (!formData.email_title.trim()) {
      newErrors.email_title = 'Message title is required.';
      isValid = false;
    }
    if (!formData.message.trim()) {
      newErrors.message = 'Message is required.';
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!validate() || !user) return;

    setLoading(true);
    try {
      await dispatch(sendMessageToUser({
        id: user.id,
        messageType: formData.message_type,
        emailTitle: formData.email_title,
        message: formData.message
      })).unwrap();

      toast.success('Message sent successfully!');
      onClose();
    } catch (error) {
      toast.error(error || 'Failed to send message');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999] animate-[fadeIn_0.3s_ease-in-out] p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg p-0 w-full max-w-[600px] max-h-[90vh] overflow-y-auto relative shadow-[0_4px_20px_rgba(0,0,0,0.15)] animate-[slideDown_0.4s_ease-out]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header flex items-center justify-between p-[15px] md:p-[20px] border-b border-[#e5e5e5] border-b-[#f4f4f4]">
          <h2 className="m-0 leading-[1.42857143] font-['Bahnschrift_Light'] text-base md:text-lg font-medium">
            Send Message
          </h2>
          <button className="close text-[21px] font-bold leading-none text-black opacity-20 hover:opacity-50 p-0 cursor-pointer bg-transparent border-0 -mt-[2px]" onClick={onClose}>×</button>
        </div>

        <form onSubmit={handleSend}>
          <div className="relative p-[15px] md:p-[20px] m-0 space-y-4">
            {/* Decoy fields for browser autofill issues */}
            <input type="text" style={{ display: 'none' }} tabIndex="-1" autoComplete="username" />
            <input type="password" style={{ display: 'none' }} tabIndex="-1" autoComplete="current-password" />

            <div className="form-group">
              <label className="inline-block max-w-full mb-[5px] font-bold text-[13px] text-[#333]">Message Type</label>
              <select
                name="message_type"
                value={formData.message_type}
                onChange={handleChange}
                className="appearance-none shadow-none border border-[#d9d9d9] rounded-[5px] text-[#4e4c4c] font-semibold text-[13px] block w-full h-[34px] px-3 py-1.5 leading-[1.42857143] bg-white transition-colors focus:border-[#ffd200] focus:ring-0 focus:outline-none"
              >
                <option value="">Select Message Type</option>
                <option value="email">Email</option>
                <option value="sms">SMS</option>
                <option value="both">Both</option>
              </select>
              {errors.message_type && <p className="text-[#ff6b6b] text-[12px] mt-1 mb-0">{errors.message_type}</p>}
            </div>

            <div className="form-group">
              <label className="inline-block max-w-full mb-[5px] font-bold text-[13px] text-[#333]">Message Title</label>
              <input
                type="text"
                name="email_title"
                value={formData.email_title}
                onChange={handleChange}
                autoComplete="off"
                className="appearance-none shadow-none border border-[#d9d9d9] rounded-[5px] text-[#4e4c4c] font-semibold text-[13px] block w-full h-[34px] px-3 py-1.5 leading-[1.42857143] bg-white transition-colors focus:border-[#ffd200] focus:ring-0 focus:outline-none"
              />
              {errors.email_title && <p className="text-[#ff6b6b] text-[12px] mt-1 mb-0">{errors.email_title}</p>}
            </div>

            <div className="form-group">
              <label className="inline-block max-w-full mb-[5px] font-bold text-[13px] text-[#333]">Message</label>
              <textarea
                name="message"
                value={formData.message}
                onChange={handleChange}
                rows="4"
                className="appearance-none shadow-none border border-[#d9d9d9] rounded-[5px] text-[#4e4c4c] font-semibold text-[13px] block w-full px-3 py-1.5 leading-[1.42857143] bg-white transition-colors focus:border-[#ffd200] focus:ring-0 focus:outline-none resize-y"
              ></textarea>
              {errors.message && <p className="text-[#ff6b6b] text-[12px] mt-1 mb-0">{errors.message}</p>}
            </div>
          </div>

          <div className="p-[15px] md:p-[20px] text-right border-t border-[#f4f4f4] flex flex-col-reverse sm:flex-row justify-between items-stretch sm:items-center gap-2">
            <button
              type="button"
              className="text-black h-9 py-2.5 px-4 text-[14px] font-['Bahnschrift',sans-serif] leading-[16.8px] bg-[#f4f4f4] text-[#444] border border-[#ddd] rounded shadow-none w-full sm:w-24 hover:bg-[#e6e6e6] transition-colors"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="text-black h-9 py-2.5 px-4 text-[14px] font-['Bahnschrift',sans-serif] leading-[16.8px] bg-gradient-to-br from-[#ffeb00] to-[#ffd200] border border-transparent rounded shadow-none w-full sm:w-24 disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-90 transition-opacity"
              disabled={loading}
            >
              {loading ? 'Sending...' : 'Send'}
            </button>
          </div>
        </form>
      </div>

      <style jsx>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideDown {
          from { transform: translateY(-100px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
      `}</style>
    </div>
  );
};

export default SendMessageModal;
